import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Input, Modal, ModalBody, ModalHeader } from "reactstrap";
import moment from "moment";
import Pagination from "@mui/material/Pagination";
import InputSearch from "../../common/Components/SearchBarField/InputSearch";
import StudioFilterBar, { readFilterSelect } from "../../common/Components/FilterBar/StudioFilterBar";
import GeoResponsiveList from "../../common/Components/GeoList/GeoResponsiveList";
import StudioRowActions, { StudioRowAction } from "../../common/Components/GeoList/StudioRowActions";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseGiRegistry } from "../../helpers/geoEnabled";
import { getGiSummary, listGiCatalog, listGis, seedJournalGis, adoptGi, unadoptGi } from "../../services/geoGiApi";
import { geoRecordId } from "./geoPath";
import { giStatusClass, giStatusLabel } from "./giStatus";
import "./gi-registry.scss";

const LIMIT = 50;

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const GiRegistryList = () => {
  const history = useHistory();
  const authUser: any = readAuthUser();
  const role = authUser?.users?.role;
  const isSuperAdmin = role === "SUPER_ADMIN";
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [name, setName] = useState("");
  const [applicationNumber, setApplicationNumber] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [totCnt, setTotCnt] = useState(0);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState<any>(null);
  const [seeding, setSeeding] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalog, setCatalog] = useState<any[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [catalogName, setCatalogName] = useState("");
  const [catalogAppNo, setCatalogAppNo] = useState("");
  const [adoptingId, setAdoptingId] = useState("");
  const [removingId, setRemovingId] = useState("");

  const loadList = useCallback(
    async (
      pageOffset = offset,
      nameFilter = name,
      appFilter = applicationNumber,
      statusFilter = status
    ) => {
      setLoading(true);
      setError("");
      try {
        const res: any = await listGis({
          offset: pageOffset,
          limit: LIMIT,
          name: nameFilter.trim() || undefined,
          applicationNumber: appFilter.trim() || undefined,
          status: statusFilter || undefined,
        });
        if (!res?.success) {
          setRows([]);
          setTotCnt(0);
          setError(res?.message || "Unable to load GI registry");
          notifications({
            title: "Error!",
            message: res?.message || "Unable to load GI registry",
            type: "danger",
          });
          return;
        }
        setRows(Array.isArray(res?.data?.docs) ? res.data.docs : []);
        setTotCnt(Number(res?.data?.length) || 0);
      } catch {
        setRows([]);
        setTotCnt(0);
        setError("Unable to load GI registry");
        notifications({
          title: "Error!",
          message: "Unable to load GI registry",
          type: "danger",
        });
      } finally {
        setLoading(false);
      }
    },
    [offset, name, applicationNumber, status]
  );

  useEffect(() => {
    if (!canUseGiRegistry(role)) {
      history.replace("/dashboard");
      return;
    }
    loadList(offset, name, applicationNumber, status);
  }, [offset, status]);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    getGiSummary().then((res: any) => {
      if (res?.success) {
        setSummary(res.data);
      }
    });
  }, [isSuperAdmin]);

  const handleSearch = () => {
    setOffset(0);
    loadList(0, name, applicationNumber, status);
  };

  const handleReset = () => {
    setName("");
    setApplicationNumber("");
    setStatus("");
    setOffset(0);
    loadList(0, "", "", "");
  };

  const handleSeedJournal = async () => {
    if (!window.confirm("Load the 46 textile GIs from GI Journal No. 29? Existing rows are updated; edited fences are kept.")) {
      return;
    }
    setSeeding(true);
    try {
      const res: any = await seedJournalGis();
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not load Journal 29 GIs",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Success!",
        message: `Journal 29: ${res?.data?.created ?? 0} created, ${res?.data?.updated ?? 0} updated`,
        type: "success",
      });
      loadList(0, name, applicationNumber, status);
      getGiSummary().then((summaryRes: any) => {
        if (summaryRes?.success) {
          setSummary(summaryRes.data);
        }
      });
    } catch {
      notifications({
        title: "Error!",
        message: "Could not load Journal 29 GIs",
        type: "danger",
      });
    } finally {
      setSeeding(false);
    }
  };

  const handlePageChange = (_event: any, value: any) => {
    setOffset(value - 1);
  };

  const totPages = Math.ceil(totCnt / LIMIT) || 0;

  const openCatalog = async () => {
    setCatalogOpen(true);
    setCatalogName("");
    setCatalogAppNo("");
    setCatalogLoading(true);
    setCatalogError("");
    try {
      const res: any = await listGiCatalog();
      if (!res?.success) {
        setCatalog([]);
        setCatalogError(res?.message || "Unable to load GI list");
        return;
      }
      setCatalog(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCatalog([]);
      setCatalogError("Unable to load GI list");
    } finally {
      setCatalogLoading(false);
    }
  };

  const filteredCatalog = useMemo(() => {
    const nameQ = catalogName.trim().toLowerCase();
    const appQ = catalogAppNo.trim().toLowerCase();
    return catalog.filter((gi: any) => {
      const matchName = !nameQ || String(gi.name || "").toLowerCase().includes(nameQ);
      const matchApp =
        !appQ || String(gi.applicationNumber || "").toLowerCase().includes(appQ);
      return matchName && matchApp;
    });
  }, [catalog, catalogName, catalogAppNo]);

  const handleAddAuthorisedUser = async (gi: any) => {
    const id = geoRecordId(gi);
    if (!id || adoptingId) {
      return;
    }
    setAdoptingId(id);
    try {
      if (!gi?.adopted && !isSuperAdmin) {
        const res: any = await adoptGi(id);
        if (!res?.success) {
          notifications({
            title: "Error!",
            message: res?.message || "Could not add this GI to your studio",
            type: "danger",
          });
          return;
        }
        notifications({
          title: "Added to studio",
          message: "This GI is part of your studio. Authorised user details can be added later.",
          type: "success",
        });
      }
      setCatalogOpen(false);
      history.push(`/gi-registry/${id}/authorised-user`);
    } catch {
      notifications({
        title: "Error!",
        message: "Could not add this GI to your studio",
        type: "danger",
      });
    } finally {
      setAdoptingId("");
    }
  };

  const handleRemove = async (gi: any) => {
    const id = geoRecordId(gi);
    const label = gi?.name || "this GI";
    if (!id || removingId) {
      return;
    }
    if (!window.confirm(`Remove ${label} from this studio? It stays in the live list, and you can add it again.`)) {
      return;
    }
    setRemovingId(id);
    try {
      const res: any = await unadoptGi(id);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not remove this GI",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Removed",
        message: `${label} is no longer in this studio.`,
        type: "success",
      });
      loadList(offset, name, applicationNumber, status);
    } catch {
      notifications({
        title: "Error!",
        message: "Could not remove this GI",
        type: "danger",
      });
    } finally {
      setRemovingId("");
    }
  };

  const giStatusChips = (ele: any, showAu: boolean) => (
    <div className="geo-chip-stack">
      <span className={giStatusClass(ele?.status, false)}>{giStatusLabel(ele?.status, false)}</span>
      {ele?.hasOpenChange ? (
        <span className={giStatusClass(ele?.status, true)}>Edits pending</span>
      ) : null}
      {ele?.adopted ? <span className="gi-status-chip is-frozen">In studio</span> : null}
      {showAu && ele?.adopted && !ele?.authorisedUserComplete ? (
        <span className="gi-status-chip is-change">AU details later</span>
      ) : null}
    </div>
  );

  const renderGiTable = (
    docs: any[],
    emptyText: string,
    actions: (ele: any, id: string) => StudioRowAction[],
    options?: { showAuthorisedUser?: boolean; picker?: boolean }
  ) => {
    const showAu = !!options?.showAuthorisedUser;
    return (
        <GeoResponsiveList
          rows={docs}
          rowKey={(ele) => geoRecordId(ele) || ele.name}
          empty={emptyText}
          tableClassName={options?.picker ? "geo-table--gi-picker" : undefined}
          actions={(ele) => <StudioRowActions actions={actions(ele, geoRecordId(ele))} />}
          columns={
            options?.picker
              ? [
                  { key: "name", header: "GI name", className: "gi-picker-name", cell: (ele) => ele?.name || "—" },
                  { key: "appNo", header: "App no.", className: "gi-picker-app", cell: (ele) => ele?.applicationNumber || "—" },
                  { key: "state", header: "State", className: "gi-picker-state", hideSm: true, cell: (ele) => ele?.state || "—" },
                  { key: "status", header: "Status", className: "gi-picker-status", cell: (ele) => giStatusChips(ele, false) },
                ]
              : [
                  {
                    key: "name",
                    header: "GI name",
                    cell: (ele) => ele?.name || "—",
                  },
                  {
                    key: "appNo",
                    header: "App no.",
                    cell: (ele) => ele?.applicationNumber || "—",
                  },
                  {
                    key: "state",
                    header: "State",
                    hideSm: true,
                    cell: (ele) => ele?.state || "—",
                  },
                  {
                    key: "status",
                    header: "Status",
                    cell: (ele) => giStatusChips(ele, showAu),
                  },
                  ...(showAu
                    ? [
                        {
                          key: "au",
                          header: "Authorised user",
                          hideSm: true,
                          cell: (ele: any) =>
                            ele?.authorisedUserComplete
                              ? ele?.authorisedUserNumber || "Saved"
                              : ele?.adopted
                              ? "Not added"
                              : "—",
                        },
                      ]
                    : []),
                  {
                    key: "added",
                    header: "Added",
                    hideSm: true,
                    cell: (ele) => (ele?.createdAt ? moment(new Date(ele.createdAt)).format("YYYY-MM-DD") : "—"),
                  },
                ]
          }
        />
    );
  };

  return (
    <div className="gi-registry-page">
      <div className="gi-toolbar-row">
        <h2>GI Registry</h2>
        <StudioFilterBar
          fields={[
            {
              id: "name",
              label: "GI name",
              placeholder: "GI name",
              value: name,
              onChange: (value: string) => setName(value),
            },
            {
              id: "applicationNumber",
              label: "App no.",
              placeholder: "Application number",
              value: applicationNumber,
              onChange: (value: string) => setApplicationNumber(value),
            },
            {
              id: "status",
              label: "Status",
              kind: "select",
              placeholder: "Status",
              value: status,
              options: [
                { label: "Pending", value: "PENDING_REVIEW" },
                { label: "Live", value: "FROZEN" },
                { label: "Changes", value: "REJECTED" },
              ],
              onChange: (value: any) => {
                setStatus(readFilterSelect(value));
                setOffset(0);
              },
            },
          ]}
          defaultFieldId="name"
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleSearch();
            }
          }}
          onReset={handleReset}
          onAdd={openCatalog}
          addAriaLabel="Add GI"
          moreCount={0}
          moreContent={
            isSuperAdmin ? (
              <div className="qf-field">
                <span className="qf-field-label">Journal</span>
                <Button className="btn-hastkala-outline" disabled={seeding} onClick={handleSeedJournal}>
                  {seeding ? "Loading…" : "Load Journal 29"}
                </Button>
              </div>
            ) : undefined
          }
          wideToolbar={
            <div className="gi-registry-filters gi-registry-filters--gi">
              <div className="geo-filter-fields">
                <InputSearch
                  name="giName"
                  placeholder="GI name"
                  value={name}
                  onChange={(e: any) => setName(e.target.value)}
                  onKeyDown={(e: any) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
                <InputSearch
                  name="giAppNo"
                  placeholder="Application number"
                  value={applicationNumber}
                  onChange={(e: any) => setApplicationNumber(e.target.value)}
                  onKeyDown={(e: any) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
              </div>
              <Input
                type="select"
                className="geo-filter-status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="">Status</option>
                <option value="PENDING_REVIEW">Pending</option>
                <option value="FROZEN">Live</option>
                <option value="REJECTED">Changes</option>
              </Input>
              <Button className="ResetBtn" onClick={handleSearch}>
                Search
              </Button>
              <Button className="btn-hastkala-outline" onClick={handleReset}>
                Reset
              </Button>
              <Button className="ResetBtn geo-toolbar-cta" onClick={openCatalog}>
                Add GI
              </Button>
              {isSuperAdmin ? (
                <Button className="btn-hastkala-outline" disabled={seeding} onClick={handleSeedJournal}>
                  {seeding ? "Loading…" : "Load Journal 29"}
                </Button>
              ) : null}
            </div>
          }
        />
      </div>
      {summary ? (
        <div className="gi-summary-strip">
          <span>Total {summary.giTotal ?? 0}</span>
          <span>Pending review {summary.giPendingReview ?? 0}</span>
          <span>Live {summary.giFrozen ?? 0}</span>
          <span>Edits pending {summary.pendingChanges ?? 0}</span>
          {typeof summary.loomTotal === "number" ? (
            <span>Looms tagged {summary.loomTotal}</span>
          ) : null}
        </div>
      ) : null}
      {error && !loading && rows.length === 0 ? (
        renderGiTable([], error, () => [], { showAuthorisedUser: !isSuperAdmin })
      ) : loading && rows.length === 0 ? (
        renderGiTable([], "Loading GIs…", () => [], { showAuthorisedUser: !isSuperAdmin })
      ) : (
        <>
          {renderGiTable(
            rows,
            isSuperAdmin
              ? "No GIs yet. Use Add GI or Load Journal 29."
              : "No GIs in your studio yet. Use Add GI to attach a live GI. A studio can work with more than one.",
            (ele, id) => {
              const actions: StudioRowAction[] = [
                {
                  key: "view",
                  label: "View",
                  icon: "view",
                  onClick: () => history.push(`/gi-registry/${id}`),
                },
                {
                  key: "edit",
                  label: "Edit",
                  icon: "edit",
                  tone: "primary",
                  onClick: () => history.push(`/gi-registry/${id}/edit`),
                },
              ];
              if (!isSuperAdmin) {
                actions.push({
                  key: "au",
                  label: "Authorised user",
                  icon: "user",
                  tone: ele?.authorisedUserComplete ? "default" : "primary",
                  onClick: () => history.push(`/gi-registry/${id}/authorised-user`),
                });
                if (ele?.adopted && !ele?.registeredByStudio) {
                  actions.push({
                    key: "remove",
                    label: "Remove",
                    icon: "delete",
                    tone: "danger",
                    disabled: removingId === id,
                    onClick: () => handleRemove(ele),
                  });
                }
              }
              return actions;
            },
            { showAuthorisedUser: !isSuperAdmin }
          )}
          {totPages > 1 ? (
            <div className="geo-pagination">
              <Pagination count={totPages} page={offset + 1} onChange={handlePageChange} />
            </div>
          ) : null}
        </>
      )}
      <Modal isOpen={catalogOpen} toggle={() => setCatalogOpen(false)} className="gi-add-modal" size="xl">
        <ModalHeader toggle={() => setCatalogOpen(false)}>
          Add GI
        </ModalHeader>
        <ModalBody>
          <p className="gi-add-lead">
            Search the live list and add each GI this studio works with. You can add more than one.
          </p>
          <div className="gi-add-search">
            <label htmlFor="catalogGiName">
              GI name
              <InputSearch
                name="catalogGiName"
                placeholder="Search by name"
                value={catalogName}
                onChange={(e: any) => setCatalogName(e.target.value)}
              />
            </label>
            <label htmlFor="catalogAppNo">
              Application number
              <InputSearch
                name="catalogAppNo"
                placeholder="App no."
                value={catalogAppNo}
                onChange={(e: any) => setCatalogAppNo(e.target.value)}
              />
            </label>
          </div>
          {catalogError ? (
            <div className="gi-form-banner">{catalogError}</div>
          ) : catalogLoading ? (
            renderGiTable([], "Loading list…", () => [], { picker: true })
          ) : (
            renderGiTable(
              filteredCatalog,
              catalogName.trim() || catalogAppNo.trim()
                ? "No GI matches. If it is not in Journal 29, register a new GI below."
                : "No GI matches that name or application number.",
              (gi, id) =>
                isSuperAdmin || (gi.adopted && gi.authorisedUserComplete)
                  ? [
                      {
                        key: "open",
                        label: "Open",
                        icon: "view" as const,
                        onClick: () => {
                          setCatalogOpen(false);
                          history.push(`/gi-registry/${id}`);
                        },
                      },
                    ]
                  : [
                      {
                        key: "add",
                        label: gi.adopted ? "Authorised user" : "Add",
                        icon: gi.adopted ? ("user" as const) : ("add" as const),
                        tone: "primary" as const,
                        onClick: () => handleAddAuthorisedUser(gi),
                      },
                    ],
              { picker: true }
            )
          )}
          <div className="geo-catalog-footer">
            <span>Not in this list? Register a new GI.</span>
            <Button
              className={
                !catalogLoading && filteredCatalog.length === 0
                  ? "ResetBtn"
                  : "btn-hastkala-outline"
              }
              onClick={() => {
                setCatalogOpen(false);
                history.push("/gi-registry/new");
              }}
            >
              Register a new GI
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default GiRegistryList;
